import React, { useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDensity } from '../../../hooks/useDensity';

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'auto';

export interface ResponsiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: ModalSize;
  title?: string;
  showCloseButton?: boolean;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  overlayClassName?: string;
  maxHeight?: string;
  preventBodyScroll?: boolean;
}

const sizeClasses = {
  xs: 'sm:max-w-xs', // 320px - alerts, confirmations
  sm: 'sm:max-w-sm', // 384px - simple forms
  md: 'sm:max-w-md', // 448px - standard forms
  lg: 'sm:max-w-2xl', // 672px - complex forms (increased from 512px)
  xl: 'sm:max-w-4xl', // 896px - large forms (increased from 576px)
  full: 'sm:max-w-7xl', // 80rem - dashboards
  auto: 'sm:max-w-fit', // Content-based
};

const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
  isOpen,
  onClose,
  children,
  size = 'lg', // Changed default from xl to lg to solve width issues
  title,
  showCloseButton = true,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  className = '',
  overlayClassName = '',
  maxHeight = 'max-h-[85vh]',
  preventBodyScroll = true,
}) => {
  const { classes, isCompact, isNormal } = useDensity();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Focus trap and accessibility
  useEffect(() => {
    if (!isOpen) return;

    // Store the previously focused element
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Focus the modal
    if (modalRef.current) {
      modalRef.current.focus();
    }

    // Prevent body scroll
    if (preventBodyScroll) {
      document.body.style.overflow = 'hidden';
    }

    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        onClose();
      }
    };

    // Focus trap
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleTabKey);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTabKey);

      // Restore body scroll
      if (preventBodyScroll) {
        document.body.style.overflow = '';
      }

      // Restore focus
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isOpen, onClose, closeOnEscape, preventBodyScroll]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOutsideClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Density-aware classes - Fixed for proper scroll behavior
  const modalClasses = classes(
    `relative transform rounded-lg bg-white text-left shadow-xl transition-all w-full z-[10041] ${sizeClasses[size]} flex flex-col ${maxHeight}`,
    className
  );

  const overlayClasses = `fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[10040] ${overlayClassName}`;

  const containerClasses = 'fixed inset-0 z-[10040] overflow-y-auto';

  const wrapperClasses = classes(
    'flex min-h-full items-end justify-center text-center sm:items-center',
    // Responsive padding based on density and screen size
    isCompact ? 'p-2 sm:p-4' : isNormal ? 'p-4 sm:p-6' : 'p-6 sm:p-8'
  );

  const headerClasses = classes(
    'flex items-center justify-between border-b border-gray-200',
    isCompact ? 'px-4 py-3' : isNormal ? 'px-6 py-4' : 'px-8 py-6'
  );

  const bodyClasses = classes(
    'flex-1 overflow-y-auto custom-scrollbar',
    isCompact ? 'px-4 py-3' : isNormal ? 'px-6 py-4' : 'px-8 py-6'
  );

  const titleClasses = classes(
    'font-semibold text-gray-900',
    isCompact ? 'text-base' : isNormal ? 'text-lg' : 'text-xl'
  );

  const closeButtonClasses = classes(
    'rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
    isCompact ? 'p-1' : 'p-2'
  );

  const modal = (
    <div className={containerClasses}>
      <div className={wrapperClasses}>
        <div className={overlayClasses} onClick={handleOverlayClick} aria-hidden="true" />

        <div
          ref={modalRef}
          className={modalClasses}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          tabIndex={-1}
        >
          {(title || showCloseButton) && (
            <div className={headerClasses}>
              {title && (
                <h3 id="modal-title" className={titleClasses}>
                  {title}
                </h3>
              )}
              {showCloseButton && (
                <button
                  type="button"
                  className={closeButtonClasses}
                  onClick={onClose}
                  aria-label="Close modal"
                >
                  <XMarkIcon className={isCompact ? 'h-5 w-5' : 'h-6 w-6'} />
                </button>
              )}
            </div>
          )}

          <div className={bodyClasses}>{children}</div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ResponsiveModal;
